import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, Check, ArrowRight, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

// ✅ STATIC SERVICES (NO BACKEND)
const STATIC_SERVICES = [
  { id: 1, name: "Haircut & Styling", price: "₹499+" },
  { id: 2, name: "Hair Color", price: "₹1499+" },
  { id: 3, name: "Cleanup / Facial", price: "₹799+" },
  { id: 4, name: "Hair Spa", price: "₹999+" },
  { id: 5, name: "Bridal Makeup", price: "₹4999+" },
  { id: 6, name: "Grooming Package", price: "₹999+" },
];

// ✅ Owner WhatsApp (change if needed)
const OWNER_WHATSAPP = "917837774865";

export default function Book() {
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ✅ Load static services once
  useEffect(() => {
    setServices(STATIC_SERVICES);
  }, []);

  const timeSlots = [
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "1:00 PM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
    "4:30 PM",
    "5:00 PM",
    "5:30 PM",
    "6:00 PM",
    "6:30 PM",
    "7:00 PM",
    "7:30 PM",
  ];

  const buildWhatsAppMessage = () => {
    const msg =
      `🔔 *New Booking Request*\n\n` +
      `👤 *Name:* ${formData.name}\n` +
      `📱 *Phone:* ${formData.phone}\n` +
      `💇 *Service:* ${formData.service || "Not specified"}\n` +
      `📅 *Date:* ${formData.preferred_date || "Not specified"}\n` +
      `⏰ *Time:* ${formData.preferred_time || "Not specified"}\n` +
      `📝 *Notes:* ${formData.notes || "None"}`;
    return msg;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error("Please fill your name and phone number");
      return;
    }

    setSubmitting(true);

    // ✅ No backend: just open WhatsApp and show success UI
    const message = buildWhatsAppMessage();
    const waUrl = `https://wa.me/${OWNER_WHATSAPP}?text=${encodeURIComponent(message)}`;

    // open WhatsApp (new tab)
    window.open(waUrl, "_blank");

    toast.success("Booking request prepared on WhatsApp. Please send to confirm.");
    setSubmitted(true);
    setSubmitting(false);
  };

  if (submitted) {
    const whatsappMessage = buildWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${OWNER_WHATSAPP}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    return (
      <div
        className="pt-24 min-h-screen flex items-center justify-center"
        data-testid="booking-success"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto section-padding"
        >
          <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-white" />
          </div>

          <h1 className="font-heading text-4xl mb-4">Booking Received!</h1>

          <p className="text-neutral-600 mb-8">
            Thank you for your booking request. Please confirm your booking on
            WhatsApp for faster response.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold flex items-center justify-center gap-2"
              data-testid="confirm-whatsapp-btn"
            >
              <MessageCircle size={18} />
              Confirm on WhatsApp
            </a>

            <Link to="/" className="btn-secondary">
              Back to Home
            </Link>
          </div>

          <p className="text-sm text-neutral-500 mt-6">
            Or call us directly at{" "}
            <a href="tel:+917837774865" className="text-gold">
              078377 74865
            </a>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24" data-testid="book-page">
      {/* Header */}
      <section className="section-padding py-16 bg-obsidian text-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">
            Schedule Your Visit
          </p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-4">
            Book Appointment
          </h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Fill in your details and we'll confirm your booking shortly
          </p>
        </motion.div>
      </section>

      <section className="section-padding section-gap">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="booking-form">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
                className="border-neutral-300 focus:border-gold rounded-none h-12"
                required
                data-testid="booking-name-input"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter your phone number"
                className="border-neutral-300 focus:border-gold rounded-none h-12"
                required
                data-testid="booking-phone-input"
              />
            </div>

            {/* Service */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Service Interested In
              </label>
              <Select
                value={formData.service}
                onValueChange={(value) => setFormData({ ...formData, service: value })}
              >
                <SelectTrigger
                  className="border-neutral-300 focus:border-gold rounded-none h-12"
                  data-testid="booking-service-select"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>

                <SelectContent>
                  {(Array.isArray(services) ? services : []).map((service) => (
                    <SelectItem key={service.id} value={service.name}>
                      {service.name} - {service.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Calendar size={16} className="inline mr-2" />
                  Preferred Date
                </label>
                <Input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) =>
                    setFormData({ ...formData, preferred_date: e.target.value })
                  }
                  min={new Date().toISOString().split("T")[0]}
                  className="border-neutral-300 focus:border-gold rounded-none h-12"
                  data-testid="booking-date-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Clock size={16} className="inline mr-2" />
                  Preferred Time
                </label>
                <Select
                  value={formData.preferred_time}
                  onValueChange={(value) =>
                    setFormData({ ...formData, preferred_time: value })
                  }
                >
                  <SelectTrigger
                    className="border-neutral-300 focus:border-gold rounded-none h-12"
                    data-testid="booking-time-select"
                  >
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>

                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Notes
              </label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special requests or preferences..."
                rows={4}
                className="border-neutral-300 focus:border-gold rounded-none resize-none"
                data-testid="booking-notes-input"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full h-14 text-base"
              data-testid="booking-submit-btn"
            >
              {submitting ? (
                "Preparing WhatsApp..."
              ) : (
                <>
                  Book Appointment <ArrowRight size={18} className="ml-2" />
                </>
              )}
            </Button>

            <p className="text-center text-sm text-neutral-500">
              Or call us directly at{" "}
              <a href="tel:+917837774865" className="text-gold hover:underline">
                078377 74865
              </a>
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
